import { useMemo, useState } from 'react'
import { ListFilter, Search, X } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import {
  IntuiDropdownMenu,
  IntuiDropdownMenuContent,
  IntuiDropdownMenuItem,
  IntuiDropdownMenuRadioGroup,
  IntuiDropdownMenuRadioItem,
  IntuiDropdownMenuSeparator,
  IntuiDropdownMenuSub,
  IntuiDropdownMenuSubContent,
  IntuiDropdownMenuSubTrigger,
  IntuiDropdownMenuTrigger,
} from '@/components/intueri/IntuiDropdownMenu'
import type { PendingSourceDocument } from '@/service/api'
import {
  activeFilterCount,
  EMPTY_FILTERS,
  type BoardOption,
  type TagMappingFilters,
} from './filters'

interface Props {
  tab: 'matched' | 'pending'
  filters: TagMappingFilters
  onChange: (filters: TagMappingFilters) => void
  /** Source sheets contributing pending tags, faceted over the whole set. */
  documents: PendingSourceDocument[]
  /** Boards the page can be scoped to. Empty while they are still loading. */
  boards: BoardOption[]
  /**
   * Fetch the board list. Called when the menu OPENS, not on page mount:
   * `GET /v1/workspaces` is unpaginated and returns every board the user can
   * see — 13,472 on staging and 109,888 on dev, per the measurements in
   * `workspaces.service.ts` — so a reader who never touches this facet must
   * not pay for it.
   */
  onOpen?: () => void
}

/**
 * How many boards the submenu will put in the DOM at once.
 *
 * A cap, not a page: with 109,888 boards on dev, mapping the list straight into
 * radio items mounts tens of thousands of elements and hangs the tab —
 * `max-h` bounds what you can SEE, not what gets rendered. Anything past the cap
 * is reached by typing, and the count of what is hidden is always shown so the
 * list never silently lies about being complete.
 */
const BOARD_MENU_LIMIT = 50

/**
 * Filter menu for Tag mapping. Facets differ per tab because the two tabs hold
 * different things: a matched pair has a method and a confidence, a pending tag
 * has neither — what it has is the sheet it was read off, which is the useful
 * cut once a plant carries a hundred-odd drawings.
 *
 * Every facet is applied server-side. Filtering the loaded page instead would
 * quietly lie: the list is paginated, so "Manual" would show only the manual
 * matches among the first 30 rows.
 *
 * Board (FS-688) is the exception to the per-tab split: it scopes WHICH FILES
 * are in view, which is a coherent question on both tabs, so it leads the menu
 * and sits outside the branch.
 */
const TagMappingFilterMenu = ({
  tab,
  filters,
  onChange,
  documents,
  boards,
  onOpen,
}: Props) => {
  const { t } = useTranslation()
  const count = activeFilterCount(filters, tab)
  const [boardQuery, setBoardQuery] = useState('')

  /**
   * Names lowercased ONCE per board list, not once per keystroke.
   *
   * Keyed on `boards`, so it survives typing. The earlier version called
   * `.toLowerCase()` inside the filter predicate, which on the 109,888-board
   * dev tenant allocated ~110k throwaway strings on every keystroke AND every
   * backspace — the DOM was bounded but the main thread was not. Comparing
   * against prepared strings leaves a keystroke doing substring checks over an
   * existing array, with no allocation.
   */
  const searchIndex = useMemo(
    () => boards.map((board) => ({ board, haystack: board.name.toLowerCase() })),
    [boards],
  )

  const matchingBoards = useMemo(() => {
    const needle = boardQuery.trim().toLowerCase()
    if (!needle) return boards
    const matches: BoardOption[] = []
    for (const entry of searchIndex) {
      if (entry.haystack.includes(needle)) matches.push(entry.board)
    }
    return matches
  }, [boards, searchIndex, boardQuery])
  const shownBoards = matchingBoards.slice(0, BOARD_MENU_LIMIT)
  const hiddenBoardCount = matchingBoards.length - shownBoards.length

  return (
    <IntuiDropdownMenu onOpenChange={(open) => open && onOpen?.()}>
      <IntuiDropdownMenuTrigger asChild>
        <IntuiButton
          variant="outline"
          size="lg"
          active={count > 0}
        >
          <ListFilter className="size-4" />
          {t('data_catalog:tag_mapping.filter', { defaultValue: 'Filter' })}
          {count > 0 && (
            <span className="ms-1 rounded-full bg-primary px-1.5 text-[11px] text-primary-foreground">
              {count}
            </span>
          )}
        </IntuiButton>
      </IntuiDropdownMenuTrigger>

      <IntuiDropdownMenuContent align="start" sideOffset={8} className="z-[1000000] w-[220px]">
        <IntuiDropdownMenuSub>
          <IntuiDropdownMenuSubTrigger>
            {t('data_catalog:tag_mapping.filters.board', { defaultValue: 'Board' })}
          </IntuiDropdownMenuSubTrigger>
          <IntuiDropdownMenuSubContent className="z-[1000000] max-h-[320px] w-[300px] overflow-y-auto">
            {/*
              Typing is how you reach a board past the cap. `stopPropagation`
              because Radix menus own the keyboard for typeahead — without it
              every keystroke jumps the highlighted item instead of reaching
              the field.
            */}
            <div className="relative p-1" onKeyDown={(e) => e.stopPropagation()}>
              <Search className="absolute start-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <IntuiInput
                value={boardQuery}
                onChange={(e) => setBoardQuery(e.target.value)}
                placeholder={t('data_catalog:tag_mapping.filters.board_search', {
                  defaultValue: 'Search boards',
                })}
                className="h-8 ps-8 text-sm"
              />
            </div>

            <IntuiDropdownMenuRadioGroup
              value={filters.boardId}
              onValueChange={(value) => onChange({ ...filters, boardId: value })}
            >
              <IntuiDropdownMenuRadioItem value="">
                {t('data_catalog:tag_mapping.filters.all_boards', {
                  defaultValue: 'All boards',
                })}
              </IntuiDropdownMenuRadioItem>
              {shownBoards.map((board) => (
                <IntuiDropdownMenuRadioItem key={board.id} value={board.id}>
                  <span className="truncate">{board.name}</span>
                </IntuiDropdownMenuRadioItem>
              ))}
            </IntuiDropdownMenuRadioGroup>

            {/* Says how many are hidden rather than just ending the list. */}
            {hiddenBoardCount > 0 && (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">
                {t('data_catalog:tag_mapping.filters.boards_truncated', {
                  defaultValue: '{{count}} more — type to narrow',
                  count: hiddenBoardCount,
                })}
              </p>
            )}
            {boards.length > 0 && matchingBoards.length === 0 && (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">
                {t('data_catalog:tag_mapping.filters.no_boards_match', {
                  defaultValue: 'No boards match.',
                })}
              </p>
            )}
          </IntuiDropdownMenuSubContent>
        </IntuiDropdownMenuSub>

        {tab === 'matched' ? (
          <>
            <IntuiDropdownMenuSub>
              <IntuiDropdownMenuSubTrigger>
                {/* "Mapped type", not "Match type": the tab is labelled Mapped and the column
                    Mapped tag, so the facet names the same thing the same way (Tag Mapping
                    review 2026-09-28, F16). */}
                {t('data_catalog:tag_mapping.filters.match_type', { defaultValue: 'Mapped type' })}
              </IntuiDropdownMenuSubTrigger>
              <IntuiDropdownMenuSubContent className="z-[1000000]">
                <IntuiDropdownMenuRadioGroup
                  value={filters.method}
                  onValueChange={(value) =>
                    onChange({ ...filters, method: value as TagMappingFilters['method'] })
                  }
                >
                  <IntuiDropdownMenuRadioItem value="">
                    {t('data_catalog:tag_mapping.filters.all', { defaultValue: 'All' })}
                  </IntuiDropdownMenuRadioItem>
                  <IntuiDropdownMenuRadioItem value="auto">
                    {t('data_catalog:tag_mapping.filters.auto', { defaultValue: 'Automatically mapped' })}
                  </IntuiDropdownMenuRadioItem>
                  <IntuiDropdownMenuRadioItem value="manual">
                    {t('data_catalog:tag_mapping.filters.manual', { defaultValue: 'Manually mapped' })}
                  </IntuiDropdownMenuRadioItem>
                </IntuiDropdownMenuRadioGroup>
              </IntuiDropdownMenuSubContent>
            </IntuiDropdownMenuSub>

            {/* Putra, 2026-09-17 (P0). Two values is the whole range on this
                tab: a match row is always `timeseries` (its type comes from a
                join over `timeseries_tags` alone), and the only `document`
                rows are the evidence lines a tag's prose mentions produce. So
                this is the control that separates matches from evidence. */}
            <IntuiDropdownMenuSub>
              <IntuiDropdownMenuSubTrigger>
                {t('data_catalog:tag_mapping.filters.source_type', { defaultValue: 'Source type' })}
              </IntuiDropdownMenuSubTrigger>
              <IntuiDropdownMenuSubContent className="z-[1000000]">
                <IntuiDropdownMenuRadioGroup
                  value={filters.sourceType}
                  onValueChange={(value) =>
                    onChange({ ...filters, sourceType: value as TagMappingFilters['sourceType'] })
                  }
                >
                  <IntuiDropdownMenuRadioItem value="">
                    {t('data_catalog:tag_mapping.filters.all', { defaultValue: 'All' })}
                  </IntuiDropdownMenuRadioItem>
                  <IntuiDropdownMenuRadioItem value="timeseries">
                    {t('data_catalog:doc_types.timeseries', { defaultValue: 'Time Series' })}
                  </IntuiDropdownMenuRadioItem>
                  <IntuiDropdownMenuRadioItem value="document">
                    {t('data_catalog:doc_types.document', { defaultValue: 'Document' })}
                  </IntuiDropdownMenuRadioItem>
                </IntuiDropdownMenuRadioGroup>
              </IntuiDropdownMenuSubContent>
            </IntuiDropdownMenuSub>

            <IntuiDropdownMenuSub>
              <IntuiDropdownMenuSubTrigger>
                {t('data_catalog:tag_mapping.filters.confidence', { defaultValue: 'Confidence' })}
              </IntuiDropdownMenuSubTrigger>
              <IntuiDropdownMenuSubContent className="z-[1000000]">
                <IntuiDropdownMenuRadioGroup
                  value={filters.confidence}
                  onValueChange={(value) =>
                    onChange({ ...filters, confidence: value as TagMappingFilters['confidence'] })
                  }
                >
                  <IntuiDropdownMenuRadioItem value="">
                    {t('data_catalog:tag_mapping.filters.all', { defaultValue: 'All' })}
                  </IntuiDropdownMenuRadioItem>
                  <IntuiDropdownMenuRadioItem value="strong">
                    {t('data_catalog:tag_mapping.filters.strong', { defaultValue: 'Strong (90%+)' })}
                  </IntuiDropdownMenuRadioItem>
                  <IntuiDropdownMenuRadioItem value="medium">
                    {t('data_catalog:tag_mapping.filters.medium', { defaultValue: 'Medium (50–89%)' })}
                  </IntuiDropdownMenuRadioItem>
                  <IntuiDropdownMenuRadioItem value="weak">
                    {t('data_catalog:tag_mapping.filters.weak', { defaultValue: 'Weak (under 50%)' })}
                  </IntuiDropdownMenuRadioItem>
                </IntuiDropdownMenuRadioGroup>
              </IntuiDropdownMenuSubContent>
            </IntuiDropdownMenuSub>
          </>
        ) : (
          <>
            <IntuiDropdownMenuSub>
              <IntuiDropdownMenuSubTrigger>
                {t('data_catalog:tag_mapping.filters.source', { defaultValue: 'Source' })}
              </IntuiDropdownMenuSubTrigger>
              <IntuiDropdownMenuSubContent className="z-[1000000] w-[220px]">
                <IntuiDropdownMenuRadioGroup
                  value={filters.source}
                  onValueChange={(value) =>
                    onChange({ ...filters, source: value as TagMappingFilters['source'] })
                  }
                >
                  <IntuiDropdownMenuRadioItem value="">
                    {t('data_catalog:tag_mapping.filters.all_sources', {
                      defaultValue: 'All sources',
                    })}
                  </IntuiDropdownMenuRadioItem>
                  <IntuiDropdownMenuRadioItem value="sheet">
                    {t('data_catalog:tag_mapping.filters.source_drawings', {
                      defaultValue: 'Drawings',
                    })}
                  </IntuiDropdownMenuRadioItem>
                  <IntuiDropdownMenuRadioItem value="document">
                    {t('data_catalog:tag_mapping.filters.source_documents', {
                      defaultValue: 'Documents',
                    })}
                  </IntuiDropdownMenuRadioItem>
                </IntuiDropdownMenuRadioGroup>
              </IntuiDropdownMenuSubContent>
            </IntuiDropdownMenuSub>

            <IntuiDropdownMenuSub>
            <IntuiDropdownMenuSubTrigger>
              {t('data_catalog:tag_mapping.filters.source_sheet', { defaultValue: 'Source sheet' })}
            </IntuiDropdownMenuSubTrigger>
            {/* Scrolls: a plant can carry hundreds of sheets. */}
            <IntuiDropdownMenuSubContent className="z-[1000000] max-h-[320px] w-[300px] overflow-y-auto">
              <IntuiDropdownMenuRadioGroup
                value={filters.documentId}
                onValueChange={(value) => onChange({ ...filters, documentId: value })}
              >
                <IntuiDropdownMenuRadioItem value="">
                  {t('data_catalog:tag_mapping.filters.all_sheets', { defaultValue: 'All sheets' })}
                </IntuiDropdownMenuRadioItem>
                {documents.map((doc) => (
                  <IntuiDropdownMenuRadioItem key={doc.document_id} value={doc.document_id}>
                    <span className="truncate">{doc.document_name}</span>
                    <span className="ms-auto ps-2 text-xs text-muted-foreground">{doc.count}</span>
                  </IntuiDropdownMenuRadioItem>
                ))}
              </IntuiDropdownMenuRadioGroup>
            </IntuiDropdownMenuSubContent>
            </IntuiDropdownMenuSub>
          </>
        )}

        {count > 0 && (
          <>
            <IntuiDropdownMenuSeparator />
            <IntuiDropdownMenuItem onClick={() => onChange(EMPTY_FILTERS)}>
              <X className="me-2 size-4 text-muted-foreground" />
              {t('data_catalog:tag_mapping.filters.clear', { defaultValue: 'Clear filters' })}
            </IntuiDropdownMenuItem>
          </>
        )}
      </IntuiDropdownMenuContent>
    </IntuiDropdownMenu>
  )
}

export default TagMappingFilterMenu
